import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { getPromotedShopItems } from '../../services/loyaltyShopApi';
import './PromotedShopModal.css';

const PromotedShopModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSuggestionModal, setShowSuggestionModal] = useState(false);
    const [hasPromotions, setHasPromotions] = useState(false);

    useEffect(() => {
        if (open) {
            loadPromotedItems();
        }
    }, [open]);

    const loadPromotedItems = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getPromotedShopItems();
            console.log('Promoted items loaded:', data);

            // If no promoted items, close the modal automatically
            if (!data || data.length === 0) {
                console.log('No promoted items found, closing modal');
                setHasPromotions(false);
                onClose();
            } else {
                setItems(data);
                setHasPromotions(true);
            }
        } catch (err) {
            console.error('Failed to load promoted items:', err);
            setError(err.message);
            setHasPromotions(false);
            // Close modal on error as well
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleClose = (closedWithButton = false) => {
        const now = new Date().toISOString();
        localStorage.setItem('promoted_shop_last_seen', now);

        if (!closedWithButton) {
            // User closed with X button - increment consecutive close counter
            const consecutiveCloses = parseInt(localStorage.getItem('promoted_modal_x_closes') || '0');
            const newCount = consecutiveCloses + 1;
            localStorage.setItem('promoted_modal_x_closes', newCount.toString());

            // If closed with X 3 times in a row, show suggestion modal
            if (newCount >= 3) {
                setShowSuggestionModal(true);
                return; // Don't close main modal yet
            }
        } else {
            // User closed with "Разбрах" button - reset consecutive closes counter
            localStorage.setItem('promoted_modal_x_closes', '0');
        }

        onClose();
    };

    const handleKeepShowing = () => {
        // Reset counter and close
        localStorage.setItem('promoted_modal_x_closes', '0');
        setShowSuggestionModal(false);
        onClose();
    };

    const handleShowLess = () => {
        // Block for 1 week
        const oneWeekFromNow = new Date();
        oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
        localStorage.setItem('promoted_modal_blocked_until', oneWeekFromNow.toISOString());
        localStorage.setItem('promoted_modal_x_closes', '0');
        setShowSuggestionModal(false);
        onClose();
    };

    if (!open || !hasPromotions) return null;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                className="promoted-shop-modal"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle className="promoted-modal-title">
                    <IconButton
                        onClick={() => handleClose(false)}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: '#fff',
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                            zIndex: 10,
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                transform: 'rotate(90deg)',
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        py: 1
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            animation: 'bounce 2s infinite'
                        }}>
                            <LocalFireDepartmentIcon sx={{ fontSize: '2.5rem', color: '#ffa500' }} />
                            <Typography variant="h4" component="span" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 2 }}>
                                {t('promotedModal.title')}
                            </Typography>
                            <LocalFireDepartmentIcon sx={{ fontSize: '2.5rem', color: '#ffa500' }} />
                        </Box>
                        <Typography variant="h6" sx={{
                            color: '#ffd700',
                            fontWeight: 600,
                            textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                        }}>
                            {t('promotedModal.subtitle')}
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent className="promoted-modal-content" sx={{ position: 'relative' }}>
                    {/* Animated stars and smoke background */}
                    <Box className="stars-background" />

                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress sx={{ color: '#ff6b00' }} size={60} />
                            </Box>
                        )}

                        {error && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="error">{error}</Typography>
                            </Box>
                        )}

                        {!loading && !error && items.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography sx={{ mb: 2 }}>Няма нови промоционални артикули в момента.</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Администраторите могат да добавят промоционални артикули от Shop Management.
                                </Typography>
                            </Box>
                        )}

                        {!loading && !error && items.length > 0 && (
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        textAlign: 'center',
                                        mb: 3,
                                        color: '#ffa500',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1
                                    }}
                                >
                                    ⚡ Специални Оферти ⚡
                                </Typography>
                                <Grid container spacing={3}>
                                    {items.map((item, index) => (
                                        <Grid item xs={12} sm={6} key={item.id}>
                                            <Card className="promoted-item-card" sx={{
                                                animation: `slideInUp 0.6s ease-out ${index * 0.1}s both`
                                            }}>
                                                <Box sx={{ position: 'relative' }}>
                                                    {/* Pulsing NEW badge */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        top: 10,
                                                        right: 10,
                                                        zIndex: 2
                                                    }}>
                                                        <Chip
                                                            icon={<StarIcon />}
                                                            label="NEW"
                                                            sx={{
                                                                fontWeight: 900,
                                                                fontSize: '1rem',
                                                                bgcolor: '#ff0000',
                                                                color: '#fff',
                                                                border: '2px solid #ffd700',
                                                                boxShadow: '0 0 20px rgba(255, 215, 0, 0.8)',
                                                                animation: 'pulse 1.5s infinite',
                                                                '& .MuiChip-icon': {
                                                                    color: '#ffd700'
                                                                }
                                                            }}
                                                        />
                                                    </Box>

                                                    {/* Discount ribbon */}
                                                    <Box className="discount-ribbon">
                                                        {t('promotedModal.specialRibbon')}
                                                    </Box>

                                                    {item.image_url && (
                                                        <CardMedia
                                                            component="img"
                                                            height="200"
                                                            image={item.image_url}
                                                            alt={item.name_bg || item.name}
                                                            className="promoted-item-image"
                                                            sx={{
                                                                objectFit: 'contain',
                                                                backgroundColor: '#1a1d1f',
                                                                filter: 'brightness(1.1) saturate(1.2)'
                                                            }}
                                                        />
                                                    )}
                                                    {!item.image_url && (
                                                        <Box
                                                            sx={{
                                                                height: 180,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: 'linear-gradient(135deg, #ff6b00 0%, #ffa500 100%)',
                                                                fontSize: '4rem'
                                                            }}
                                                        >
                                                            🎁
                                                        </Box>
                                                    )}
                                                </Box>
                                                <CardContent sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    height: '100%',
                                                    pb: 2
                                                }}>
                                                    <Typography variant="h6" component="div" sx={{
                                                        fontWeight: 700,
                                                        mb: 1,
                                                        color: '#fff',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                                                    }}>
                                                        {item.name_bg || item.name}
                                                    </Typography>

                                                    <Box sx={{ flexGrow: 1, mb: 2 }}>
                                                        {item.description_bg && (
                                                            <Typography variant="body2" sx={{
                                                                color: 'rgba(255, 255, 255, 0.85)',
                                                                lineHeight: 1.6
                                                            }}>
                                                                {item.description_bg}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        pt: 2,
                                                        borderTop: '2px solid rgba(255, 107, 0, 0.3)'
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'baseline',
                                                            gap: 1
                                                        }}>
                                                            <Typography variant="h5" sx={{
                                                                color: '#ffd700',
                                                                fontWeight: 900,
                                                                textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                                                            }}>
                                                                {item.hookah_cost}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: '#ffd700' }}>
                                                                {item.hookah_cost === 1 ? t('promotedModal.hookah') : t('promotedModal.hookahs')}
                                                            </Typography>
                                                        </Box>
                                                        {item.stock > 0 && (
                                                            <Chip
                                                                label={`Налични: ${item.remaining_stock || item.stock}`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'rgba(76, 175, 80, 0.2)',
                                                                    color: '#4caf50',
                                                                    border: '1px solid #4caf50',
                                                                    fontWeight: 600
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions className="promoted-modal-actions" sx={{ justifyContent: 'center', py: 2.5 }}>
                    <Button
                        onClick={() => handleClose(true)}
                        variant="contained"
                        size="large"
                        sx={{
                            bgcolor: '#ff6b00',
                            px: 5,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            boxShadow: '0 4px 20px rgba(255, 107, 0, 0.4)',
                            '&:hover': {
                                bgcolor: '#ff8533',
                                transform: 'scale(1.05)',
                                boxShadow: '0 6px 30px rgba(255, 107, 0, 0.6)',
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {t('promotedModal.gotItButton')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Suggestion Modal - shown after 3 consecutive X closes */}
            <Dialog
                open={showSuggestionModal}
                onClose={handleKeepShowing}
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        bgcolor: '#2a2d31',
                        border: '2px solid #ff6b00'
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: '#1a1d1f',
                    color: '#fff',
                    borderBottom: '1px solid #ff6b00',
                    textAlign: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('promotedModal.suggestionModal.title')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ mt: 3, color: '#fff' }}>
                    <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', lineHeight: 1.8 }}>
                        {t('promotedModal.suggestionModal.message1')}
                    </Typography>
                    <Typography variant="body1" sx={{ textAlign: 'center', lineHeight: 1.8 }} dangerouslySetInnerHTML={{ __html: t('promotedModal.suggestionModal.message2') }} />
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1.5, p: 3 }}>
                    <Button
                        onClick={handleShowLess}
                        variant="contained"
                        fullWidth
                        sx={{
                            bgcolor: '#ff6b00',
                            color: '#fff',
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: '#ff8533'
                            }
                        }}
                    >
                        {t('promotedModal.suggestionModal.showLessButton')}
                    </Button>
                    <Button
                        onClick={handleKeepShowing}
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderColor: '#ff6b00',
                            color: '#ff6b00',
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&:hover': {
                                borderColor: '#ff8533',
                                color: '#ff8533',
                                bgcolor: 'rgba(255, 107, 0, 0.1)'
                            }
                        }}
                    >
                        {t('promotedModal.suggestionModal.keepShowingButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PromotedShopModal;
